import config from "Configurations";
import { apiRequest } from "util/services";

const BASE = "feedback";

async function call(url, method = "GET", payload = null) {
  const res = await apiRequest(url, method, payload, true);
  if (!res || res.error) {
    const err = new Error(res?.error || "request_failed");
    err.code = res?.error || "request_failed";
    err.status = res?.httpStatus;
    throw err;
  }
  return res;
}

// --- console (signed in) ---

export const getCalendar = (botId) => call(`${BASE}/instruments/${botId}/calendar`);

export const saveCalendar = (botId, { enabled, config: cfg, bookingSlug }) =>
  call(`${BASE}/instruments/${botId}/calendar`, "PUT", {
    enabled,
    config: cfg,
    booking_slug: bookingSlug || null,
  });

export const previewSlots = (botId, { serviceId, staffId, from, days = 7 }) =>
  call(
    `${BASE}/instruments/${botId}/calendar/slots?service_id=${encodeURIComponent(serviceId || "")}` +
      `&from=${from || ""}&days=${days}` +
      (staffId ? `&staff_id=${encodeURIComponent(staffId)}` : "")
  );

export const listBookings = (botId, scope = "upcoming") =>
  call(`${BASE}/instruments/${botId}/calendar/bookings?scope=${scope}`);

export const createBooking = (botId, { serviceId, staffId, start, name, phone, email, locale }) =>
  call(`${BASE}/instruments/${botId}/calendar/bookings`, "POST", {
    service_id: serviceId,
    start,
    name,
    phone,
    email,
    locale,
    ...(staffId ? { staff_id: staffId } : {}),
  });

export const cancelBooking = (botId, bookingId) =>
  call(`${BASE}/instruments/${botId}/calendar/bookings/${bookingId}/cancel`, "POST", {});

export const markNoShow = (botId, bookingId) =>
  call(`${BASE}/instruments/${botId}/calendar/bookings/${bookingId}/no_show`, "POST", {});

export const addKnownCustomers = (botId, text) =>
  call(`${BASE}/instruments/${botId}/calendar/customers`, "POST", { text });

export const clearKnownCustomers = (botId) => call(`${BASE}/instruments/${botId}/calendar/customers`, "DELETE");

export const confirmCalendarConnection = (botId, code) =>
  call(`${BASE}/instruments/${botId}/calendar/connection/confirm`, "POST", { code });

export const rescheduleBooking = (botId, bookingId, start) =>
  call(`${BASE}/instruments/${botId}/calendar/bookings/${bookingId}/reschedule`, "POST", { start });

export const startOAuth = (botId, provider) =>
  call(`${BASE}/prospect/calendar/${provider}/oauth/start?instrument_id=${botId}`);

export const connectCalendly = (botId, token) =>
  call(`${BASE}/instruments/${botId}/calendar/calendly`, "POST", { token });

export const listCalendars = (botId) => call(`${BASE}/instruments/${botId}/calendar/calendars`);

export const saveConnection = (botId, { busyCalendarIds, writeCalendarId }) =>
  call(`${BASE}/instruments/${botId}/calendar/connection`, "PUT", {
    busy_calendar_ids: busyCalendarIds,
    write_calendar_id: writeCalendarId,
  });

export const disconnectCalendar = (botId) =>
  call(`${BASE}/instruments/${botId}/calendar/connection`, "DELETE");

export const upcomingCounts = () => call(`${BASE}/prospect/calendar/upcoming`);

// --- public pages (no session): plain fetch so a visitor is never sent to the login page ---

async function publicCall(path, method = "GET", payload = null) {
  const init = { method, headers: { "Content-Type": "application/json" } };
  if (payload) init.body = JSON.stringify(payload);
  const resp = await fetch(`${config.BASE_URL}${BASE}${path}`, init);
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(body?.error || `HTTP ${resp.status}`);
    err.code = body?.error || "request_failed";
    err.status = resp.status;
    throw err;
  }
  return body;
}

export const getBookingPage = (slug) => publicCall(`/public/calendar/page/${encodeURIComponent(slug)}`);

export const getBookingPageSlots = (slug, { serviceId, staffId, from, days = 7, locale }) =>
  publicCall(
    `/public/calendar/page/${encodeURIComponent(slug)}/slots?service_id=${encodeURIComponent(serviceId)}` +
      `&from=${from || ""}&days=${days}&locale=${locale || ""}` +
      (staffId ? `&staff_id=${encodeURIComponent(staffId)}` : "")
  );

export const bookFromPage = (slug, payload) =>
  publicCall(`/public/calendar/page/${encodeURIComponent(slug)}/book`, "POST", payload);

export const getAppointment = (token) => publicCall(`/public/calendar/appointment/${encodeURIComponent(token)}`);

export const getAppointmentSlots = (token, { from, days = 7 }) =>
  publicCall(`/public/calendar/appointment/${encodeURIComponent(token)}/slots?from=${from || ""}&days=${days}`);

export const cancelAppointment = (token) =>
  publicCall(`/public/calendar/appointment/${encodeURIComponent(token)}/cancel`, "POST", {});

export const rescheduleAppointment = (token, start) =>
  publicCall(`/public/calendar/appointment/${encodeURIComponent(token)}/reschedule`, "POST", { start });
